import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Alert, 
  Avatar, 
  Box, 
  Chip, 
  LinearProgress, 
  Pagination, 
  Paper, 
  Snackbar, 
  SnackbarCloseReason, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableFooter, 
  TableHead, 
  TableRow,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { FerramentasDaListagemCadastros } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemCadastros';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { IListagemUsuarios, UsuarioService } from '../../../shared/services/api/cadastros/UsuariosService';

export const ListagemUsuarios: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();

  const [rows, setRows] = useState<IListagemUsuarios[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'usuarios';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHAS);
  const [containerHeight, setContainerHeight] = useState<number>();
  const tabelaRef = useRef<HTMLDivElement>(null);
  const rowHeightRef = useRef<number>(0);
  const headerHeightRef = useRef<number>(0);
  const footerHeightRef = useRef<number>(0);

  const calcularAlturaContainer = () => {
    const container = tabelaRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de "calc(100vh - Npx)",
    // que ficava desalinhado com a altura real do título/toolbar e causava scroll na página inteira.
    // Só é recalculado em um resize real da janela (não a cada render), para a parte superior não
    // ficar se reposicionando sozinha e gerando atualizações em loop na listagem.
    const BOTTOM_GAP = 16;
    const novaAltura = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);
    setContainerHeight(prev => (prev === novaAltura ? prev : novaAltura));
  };

  const calcularLimiteLinhas = () => {
    const container = tabelaRef.current;
    if (!container) return;

    const headerEl = container.querySelector('thead');
    const rowEl = rows.length > 0 ? container.querySelector('tbody tr') : null;
    const footerEl = container.querySelector('tfoot');

    if (headerEl) headerHeightRef.current = headerEl.getBoundingClientRect().height;
    if (rowEl) rowHeightRef.current = rowEl.getBoundingClientRect().height;
    if (footerEl) footerHeightRef.current = footerEl.getBoundingClientRect().height;

    if (!rowHeightRef.current || !containerHeight) return;

    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    // Usa o orçamento de altura calculado (containerHeight), não container.clientHeight:
    // quando a listagem tem poucos itens o container encolhe (height: 'auto'), e
    // clientHeight passaria a refletir só o conteúdo atual em vez do espaço disponível.
    const alturaDisponivel = containerHeight - headerHeightRef.current - footerHeightRef.current;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / rowHeightRef.current) - FOLGA_LINHAS)
    );

    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  useEffect(() => {
    calcularAlturaContainer();
    window.addEventListener('resize', calcularAlturaContainer);
    return () => window.removeEventListener('resize', calcularAlturaContainer);
  }, []);

  // Depende de totalCount (não de "rows") para não reagendar a cada refetch/página,
  // o que causava um looping de recálculo quando a altura medida variava por
  // arredondamento (ex: 1 item sem paginação, ou fetches acumulados ao voltar de outra aba).
  useEffect(() => {
    const id = setTimeout(calcularLimiteLinhas, 50);
    return () => clearTimeout(id);
  }, [totalCount, containerHeight]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  // Extrai os parâmetros de busca
  const tipo = useMemo(() => searchParams.get('tipo') || 'nome', [searchParams]);
  const filter = useMemo(() => searchParams.get('filter') || '', [searchParams]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      UsuarioService.getAll(pagina, filter, rowsPerPage)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setMsgType('error');
          setMsg('Erro ao carregar dados. Tente novamente mais tarde.');
          setOpenMsg(true);
        });
    });
  }, [pagina, filter, rowsPerPage, debounce]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, tipo, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  return (
    <LayoutBaseDePagina
      titulo='Listagem de Usuários'
      barraDeFerramentas={
        <FerramentasDaListagemCadastros
          mostrarInputBusca
          mostrarInputBasicSelect
          textoDaBusca={filter}
          textoDoSelect={tipo}
          textoBotaoNovo='Novo Usuário'
          aoClicarEmNovo={() => navigate('/usuarios/novo')}
          aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo, pagina: '1' }, { replace: true })}
          aoMudarTextoDoSelect={textoselect => setSearchParams({ filter, tipo: textoselect, pagina: '1' }, { replace: true })}
          dadosSelecao={[
            { nameBD: 'nome', descricao: 'Nome' },
            { nameBD: 'login', descricao: 'Login' },
          ]} 
        />
      }
    >
      {/* Notificação */}
      <Snackbar 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
        open={openMsg}  
        autoHideDuration={6000} 
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={msgType}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      {/* Container principal com sombra suave e bordas arredondadas */}
      <Paper 
        elevation={1} 
        sx={{ 
          borderRadius: 2, 
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <TableContainer
          ref={tabelaRef}
          sx={{
            // 'auto' quando a página atual não preenche rowsPerPage (única página curta ou
            // última página parcial de uma listagem paginada), para o fundo acompanhar a
            // quantidade real de itens em vez de esticar até o fim da tela.
            height: rows.length >= rowsPerPage
              ? (containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)')
              : 'auto',
            maxHeight: containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)',
            overflow: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                '& th': { 
                  fontWeight: 'bold', 
                  color: theme.palette.primary.dark,
                  fontSize: '0.95rem',
                  py: 2
                }
              }}>
                <TableCell sx={{ width: 70 }}></TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Login</TableCell>
                <TableCell align='center' sx={{ width: 120 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            
            <TableBody>
              {rows.map((row) => (
                <TableRow 
                  key={row.iduser} 
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.03)
                    },
                    '&:last-child td': { borderBottom: 0 }
                  }}
                  onClick={() => navigate(`/usuarios/${row.iduser}`)}
                >
                  <TableCell>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40,
                        margin: 'auto'
                      }}  
                      alt={row.nome} 
                      src={Environment.URL_IMGUSUARIOS +row.urlimg}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant='body1' fontWeight='medium' noWrap>
                      {row.nome}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant='body1' noWrap>
                      {row.login}
                    </Typography>
                  </TableCell>
                  
                  <TableCell align='center'>
                    <Chip 
                      label={row.status ? 'Ativo' : 'Inativo'} 
                      size='small' 
                      color={row.status ? 'success' : 'error'} 
                      variant='outlined'
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
  
            {/* Mensagem de lista vazia */}
            {totalCount === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                    <Box>
                      <Typography variant='h6' color='text.secondary' gutterBottom>
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Novo Usuário" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
            
            <TableFooter>
              {/* Loading indicator */}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              
              {/* Paginação */}
              {(totalCount > 0 && totalCount > rowsPerPage) && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 2 }}>
                    <Box display='flex' justifyContent='center' alignItems='center' px={2}>
                      <Pagination
                        page={pagina}
                        count={Math.ceil(totalCount / rowsPerPage)}
                        onChange={(_, newPage) => setSearchParams(
                          { filter, tipo, pagina: newPage.toString() }, 
                          { replace: true }
                        )}
                        color='primary'
                        shape='rounded'
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};